import equityBundle from './data/equityV6CompactAuthored.json';
import {
  applyElectricityPriceEquityNote,
  type ElectricityProviderClass,
} from './equityElectricityPriceRule';
import {
  flattenEquityV6Compact,
  type EquityV6Compact,
} from './equityV6Format';

type EquityV6Row = {
  chart_id: string;
  view_id: string;
  equity_rule_id?: string;
  equity: EquityV6Compact;
};

type EquityV6Bundle = {
  framework_version: string;
  rows: EquityV6Row[];
};

const bundle = equityBundle as EquityV6Bundle;

const compactByKey = new Map<string, EquityV6Compact>();
const ruleByKey = new Map<string, string>();

for (const row of bundle.rows) {
  const key = `${row.chart_id}|${row.view_id}`;
  if (!compactByKey.has(key)) {
    compactByKey.set(key, row.equity);
  }
  if (row.equity_rule_id && !ruleByKey.has(key)) {
    ruleByKey.set(key, row.equity_rule_id);
  }
}

export type { EquityV6Compact };

export type EquityResolveContext = {
  electricityPriceSector?: string;
  electricityProviderClass?: ElectricityProviderClass;
  /** Optional exact-authored supplemental sentences (omit when facts unavailable). */
  electricityOptionalBurdenSentence?: string | null;
  electricityOptionalGasCompareSentence?: string | null;
};

/** Runtime view ids that should inherit matrix equity from another view on the same chart. */
const VIEW_ALIASES: Record<string, Record<string, string>> = {
  estimated_driving_fuel_cost: { fuel_cost: 'default' },
  transport_map: { zero_vehicle_households: 'vehicles_per_household' },
};

/**
 * When chart_id in UI differs from authoring matrix chart_id, resolve equity from the canonical chart.
 */
const CHART_EQUITY_FALLBACKS: Array<{
  chartId: string;
  viewId: string;
  fallbackChartId: string;
  fallbackViewId: string;
}> = [
  {
    chartId: 'ev_fleet_adoption',
    viewId: 'perCapita',
    fallbackChartId: 'electric_vehicle_adoption',
    fallbackViewId: 'default',
  },
  {
    chartId: 'ev_fleet_adoption',
    viewId: 'projection',
    fallbackChartId: 'electric_vehicle_adoption',
    fallbackViewId: 'default',
  },
];

function resolveViewId(chartId: string, viewId: string): string {
  return VIEW_ALIASES[chartId]?.[viewId] ?? viewId;
}

function lookupCompact(chartId: string, viewId: string): EquityV6Compact | undefined {
  const resolvedView = resolveViewId(chartId, viewId);
  const key = `${chartId}|${resolvedView}`;
  const direct = compactByKey.get(key);
  if (direct) return direct;
  return compactByKey.get(`${chartId}|default`);
}

function applySmartEquityRules(
  chartId: string,
  viewId: string,
  base: EquityV6Compact,
  context?: EquityResolveContext
): EquityV6Compact {
  const resolvedView = resolveViewId(chartId, viewId);
  const ruleId = ruleByKey.get(`${chartId}|${resolvedView}`);
  if (ruleId === 'EQ_ELECTRICITY_PRICE' && chartId === 'electricity_prices') {
    return applyElectricityPriceEquityNote(base, {
      electricityOptionalBurdenSentence: context?.electricityOptionalBurdenSentence,
      electricityOptionalGasCompareSentence: context?.electricityOptionalGasCompareSentence,
    });
  }
  return base;
}

export function resolveEquityV6Compact(
  chartId: string,
  viewId: string,
  context?: EquityResolveContext
): EquityV6Compact | undefined {
  let compact = lookupCompact(chartId, viewId);

  if (!compact) {
    for (const fb of CHART_EQUITY_FALLBACKS) {
      if (fb.chartId === chartId && fb.viewId === viewId) {
        compact = lookupCompact(fb.fallbackChartId, fb.fallbackViewId);
        if (compact) break;
      }
    }
  }

  if (!compact) return undefined;
  return applySmartEquityRules(chartId, viewId, compact, context);
}

/**
 * Authored equity copy precedence: exact chart+view, then section default, then chart fallbacks.
 * Returns undefined when no authored row exists (caller keeps template/closeout text).
 */
export function resolveEquityV2(
  chartId: string,
  viewId: string,
  context?: EquityResolveContext
): string | undefined {
  const compact = resolveEquityV6Compact(chartId, viewId, context);
  if (!compact) return undefined;
  return flattenEquityV6Compact(compact);
}

export const EQUITY_V2_FRAMEWORK_VERSION = bundle.framework_version;

export const LEGACY_EQUITY_BOILERPLATE_PATTERNS: RegExp[] = [
  /community-level indicator and does not by itself show/i,
  /Equity interpretation should use disaggregated data/i,
  /Overview cards should not add new equity conclusions/i,
];

export function equityTextHasLegacyBoilerplate(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  return LEGACY_EQUITY_BOILERPLATE_PATTERNS.some((re) => re.test(t));
}

/** Authored v6 copy should not use em dashes (authoring QA rule). */
export function equityTextHasEmDash(text: string): boolean {
  return /[\u2013\u2014]/.test(text);
}

export function listEquityV2Keys(): string[] {
  return [...compactByKey.keys()].sort();
}
